import React from 'react'
import { motion } from 'framer-motion'
import { Chrome, ArrowRight, Globe, Shield, Zap, Users, CheckCircle, Sparkles } from 'lucide-react'

interface ReadyToOrganizeCTAProps {
  headline?: string
  subtext?: string
  onLearnMoreClick?: () => void
}

const ReadyToOrganizeCTA: React.FC<ReadyToOrganizeCTAProps> = ({
  headline = 'Ready to organize your browser tabs?',
  subtext = 'This session was created with Chrono Session. Transform your browsing experience and never lose important tabs again.',
  onLearnMoreClick
}) => (
  <motion.div 
    className="mt-8 sm:mt-16 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8 md:p-12 relative overflow-hidden"
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: 0.5 }}
  >
    {/* Background Pattern */}
    <div className="absolute inset-0 opacity-30 pointer-events-none">
      <div className="absolute inset-0 bg-blue-100/20" style={{
        backgroundImage: 'radial-gradient(circle at 20px 20px, #3b82f6 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }}></div>
    </div>
    <div className="relative text-center max-w-4xl mx-auto">
      <div className="inline-flex items-center px-3 py-1 sm:px-4 sm:py-2 bg-blue-100 text-blue-700 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
        <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
        Join 100K+ professionals
      </div>
      <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight text-gray-900 px-2">
        {headline}
      </h3>
      <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto px-2">
        {subtext}
      </p>
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8 px-2">
        <a 
          href="https://chrome.google.com/webstore/detail/mmoeppckalbggegohbocmbkpfmnocpcd"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg text-base sm:text-lg w-full sm:w-auto"
        >
          <Chrome className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
          <span className="hidden sm:inline">Add to Chrome - Free</span>
          <span className="sm:hidden">Add to Chrome</span>
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 sm:ml-3" />
        </a>
        <button 
          onClick={onLearnMoreClick || (() => window.open('/', '_blank'))}
          className="inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:border-blue-400 hover:text-blue-600 transition-all duration-300 text-base sm:text-lg w-full sm:w-auto"
        >
          <Globe className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
          Learn More
        </button>
      </div>
      <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-600 px-2">
        <div className="flex items-center">
          <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-blue-600" />
          <span className="hidden sm:inline">Enterprise Security</span>
          <span className="sm:hidden">Security</span>
        </div>
        <div className="flex items-center">
          <Zap className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-blue-600" />
          <span className="hidden sm:inline">Lightning Fast</span>
          <span className="sm:hidden">Fast</span>
        </div>
        <div className="flex items-center">
          <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-blue-600" />
          <span className="hidden sm:inline">100K+ Users</span>
          <span className="sm:hidden">100K+</span>
        </div>
        <div className="flex items-center">
          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-blue-600" />
          <span className="hidden sm:inline">Free Forever</span>
          <span className="sm:hidden">Free</span>
        </div>
      </div>
    </div>
  </motion.div>
)

export default ReadyToOrganizeCTA 